<template>
  <ModalStep
    :id="'session-details-modal-content'"
    data-test-id="session-details-modal-content"
    :onExitHandler="onExitHandler"
    :header="{
      title: sessionRef?.name ? sessionRef.name : t('common.session.capitalize'),
    }"
  >
    <template #headerAfterElement>
      <Badge v-if="sessionRef?.status" :variant="badgePropsRef.variant">
        {{ badgePropsRef.textKey ? t(badgePropsRef.textKey) : '' }}
      </Badge>
    </template>
    <template #bodyContent>
      <component v-for="component in activeBodyComponentsRef" :key="component.name" :is="component" />
    </template>

    <template #footer v-if="activeFooterComponentRef">
      <component :is="activeFooterComponentRef" />
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programStore from '@/features/programs/stores/programs/programs.store'
import SessionDeliverables from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-deliverables/session-deliverables.vue'
import SessionGeneralInformations from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-general-informations/session-general-informations.vue'
import SessionOwnDeliverables from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-own-deliverables/session-own-deliverables.vue'
import SessionParticipants from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-participants/session-participants.vue'
import SessionPendingActions from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-pending-actions/session-pending-actions.vue'
import SessionReport from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-report/session-report.vue'
import SessionMenteeReport from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-mentee-report/session-mentee-report.vue'
import SessionReviews from '@/features/sessions/components/session-details-modal/session-details-modal-content/components/session-reviews/session-reviews.vue'
import SessionDetailsPlannedFooter from '@/features/sessions/components/session-details-modal/session-details-modal-content/footers/session-details-planned-footer.vue'
import SessionDetailsWaitConfirmationFooter from '@/features/sessions/components/session-details-modal/session-details-modal-content/footers/session-details-wait-confirmation-footer.vue'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import { getSessionStatusBadgeProps } from '@/features/sessions/utils'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import Badge from '@/components/Badge.vue'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import checkPermissionsGroup from '@/features/users/utils'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

defineProps<{
  onExitHandler: (callback?: () => void) => void
}>()

const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)

const selectedProgramIdRef = selectGetSelectedProgramId(programStore)
const { programRef } = useProgram(selectedProgramIdRef)
const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)
const { sessionRef } = useSession(sessionIdRef)
const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const badgePropsRef = computed(() => {
  if (!sessionRef.value?.status) return { variant: 'black' as const, textKey: '' }
  return getSessionStatusBadgeProps(sessionRef.value.status)
})
const { programRolesRef } = useProgramRoles({ programIdRef: selectedProgramIdRef })

// active sidebar popup body component
// based on roles and session status
const activeBodyComponentsRef = computed(() => {
  // check if user has admin role at program level (for /admin/sessions page)
  const isProgramAdmin = checkPermissionsGroup(programRolesRef.value, PermissionsGroupsEnumType.ADMIN)
  // check if user has admin role at group level
  const isGroupAdmin = checkPermissionsGroup(groupRolesRef.value, PermissionsGroupsEnumType.ADMIN)
  // check if user has user role at group level (mentor/mentee)
  const isGroupUser = checkPermissionsGroup(groupRolesRef.value, PermissionsGroupsEnumType.USER)

  // admin views: show admin view if user is program admin WITHOUT group role, OR if user is group admin
  if ((isProgramAdmin && !isGroupUser) || isGroupAdmin) {
    const sessionStatus = sessionRef.value?.status
    switch (true) {
      case sessionStatus === SessionStatusEnumType.FINISHED || sessionStatus === SessionStatusEnumType.ONGOING:
        const adminViews = []

        // push general informations and participants
        adminViews.push(SessionGeneralInformations, SessionParticipants)

        // if report option is enabled, we add the report
        if (programRef.value?.settings.hasMentorReviewSessionDeliverableReport) {
          adminViews.push(SessionReport)
        }

        // if mentee report option is enabled, we add the mentee report
        if (programRef.value?.settings.hasMenteeReviewSessionReport) {
          adminViews.push(SessionMenteeReport)
        }

        // if deliverable option is enabled, we add the deliverables
        if (
          programRef.value?.settings.hasReviewSessionDeliverable ||
          programRef.value?.settings.shouldMentorReviewSessionDeliverableRate
        ) {
          adminViews.push(SessionDeliverables)
        }

        // if reviews option is enabled, we add the reviews
        if (programRef.value?.settings.hasReviewSessionRating || programRef.value?.settings.hasReviewSessionReview) {
          adminViews.push(SessionReviews)
        }

        return adminViews

      default:
        return [SessionGeneralInformations, SessionParticipants]
    }
    // users views: show user view if user has group role (mentor/mentee)
  } else if (isGroupUser) {
    const sessionStatus = sessionRef.value?.status
    switch (true) {
      // if the session is in review or finished, we addd details
      case sessionStatus === SessionStatusEnumType.WAIT_REVIEW:
        const waitReviewViews = []
        // first, we add the pending actions
        waitReviewViews.push(SessionPendingActions)

        // if the user is a mentor and the program has review deliverable options, we add the deliverables
        if (
          groupRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) &&
          programRef.value?.settings.hasReviewSessionDeliverable
        ) {
          waitReviewViews.push(SessionDeliverables)
        }

        // if the user is a mentee and the program has review deliverable options, we add the deliverables
        if (
          groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) &&
          programRef.value?.settings.hasReviewSessionDeliverable
        ) {
          waitReviewViews.push(SessionOwnDeliverables)
        }

        // push general informations and participants
        waitReviewViews.push(SessionGeneralInformations, SessionParticipants)
        return waitReviewViews

      case sessionStatus === SessionStatusEnumType.FINISHED:
        const finishedViews = []

        // if the user is a mentor and the program has review deliverable options, we add the deliverables
        if (
          groupRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) &&
          programRef.value?.settings.hasReviewSessionDeliverable
        ) {
          finishedViews.push(SessionDeliverables)
        }

        // if the user is a mentee and the program has review deliverable options, we add the deliverables
        if (
          groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) &&
          programRef.value?.settings.hasReviewSessionDeliverable
        ) {
          finishedViews.push(SessionOwnDeliverables)
        }

        // if session is finished and option is enabled, show report
        if (
          sessionStatus === SessionStatusEnumType.FINISHED &&
          programRef.value?.settings.hasMentorReviewSessionDeliverableReport
        ) {
          finishedViews.push(SessionReport)
        }

        // if session is finished and mentee report option is enabled, show mentee report
        if (
          sessionStatus === SessionStatusEnumType.FINISHED &&
          programRef.value?.settings.hasMenteeReviewSessionReport
        ) {
          finishedViews.push(SessionMenteeReport)
        }

        // push general informations and participants
        finishedViews.push(SessionGeneralInformations, SessionParticipants)

        // then the pending actions
        finishedViews.push(SessionPendingActions)

        return finishedViews

      default:
        // if not in review or finished, we only add general informations and participants
        return [SessionGeneralInformations, SessionParticipants]
    }
  }
  return []
})

// active sidebar popup footer component
// based on roles and session status
const activeFooterComponentRef = computed(() => {
  // check if user has admin role at program level (for /admin/sessions page)
  const isProgramAdmin = checkPermissionsGroup(programRolesRef.value, PermissionsGroupsEnumType.ADMIN)
  // check if user has admin role at group level
  const isGroupAdmin = checkPermissionsGroup(groupRolesRef.value, PermissionsGroupsEnumType.ADMIN)
  // check if user has user role at group level (mentor/mentee)
  const isGroupUser = checkPermissionsGroup(groupRolesRef.value, PermissionsGroupsEnumType.USER)

  // admin views (they don't have footer actions atm)
  if ((isProgramAdmin && !isGroupUser) || isGroupAdmin) {
    return null

    // users views
  } else if (isGroupUser) {
    switch (sessionRef.value?.status) {
      case SessionStatusEnumType.PLANNED:
        return SessionDetailsPlannedFooter

      case SessionStatusEnumType.WAIT_CONFIRMATION:
        return SessionDetailsWaitConfirmationFooter

      // case SessionStatusEnumType.WAIT_REVIEW:
      //   return SessionDetailsWaitReviewFooter

      default:
        return null
    }
  }
  return null
})
</script>

<style lang="scss">
@import './session-details-modal-content.scss';
</style>
